// =============================================================================
// Copyright   : 2022 semantics GmbH
// Date        : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
// Author      : Dirk Rothe
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 156393 $
// Purpose     : pagebox panels
// =============================================================================
// ---Third-party imports
import React, {createElement} from 'react'
import {Menu, Segment, Message, Card, Image} from 'semantic-ui-react'

// ---Framework imports
import {Actions, Sel} from 'uix/registries'
import log from 'uix/logging'
import {mapItems, size, get, dict, keys, filterItems, isNil} from 'ldash'
import {XMenu, HeaderSegment} from 'uix/components'
import {
  useApi,
  useCallApi,
  useResettableState,
  usePhysicalNode,
  useSelector,
  useInView,
  useState,
  useEffect,
  usePersistent,
} from 'uix/hooks'
import {useTreeNodeFields, useTreeList, useTreeNodes} from 'uix/db/hooks'
import {DashSegment} from 'uix/components/grid'
import {BoundingBoxViewer} from 'uix/viewer/components'
import {TreeNode} from 'uix/types/components'

// ---Local Imports

// ---Exports
const BOXTYPES = i18n => ({
  /* configure known boxtypes
   */
  ocr: {datatype: [3], isLink: false, opbtype: 2, label: 'OCR eingegrenzt', collapsed: true},
  titlepage: {
    datatype: [],
    type: ['collection', 'classification', 'exhibition'],
    opbtype: 3,
    label: 'Titelseite',
  },
  //~ background: {datatype: [], type: ['room', 'scene'], opbtype: 4, label: 'Hintergrund'},
  slideasset: {
    datatype: [3],
    parentType: ['room', 'scene'],
    isLink: true,
    opbtype: 5,
    label: 'Ausschnitt',
  },
})

const ImagePreview = ({opbid, width = 1000}) => {
  let apiurl = useSelector(Sel.apiurl)
  return <Image width={`${width}px`} src={apiurl.href(`/image/pagebox/${opbid}/${width}`)} />
}

const ImageSelect = ({otid, opbtype, onSelect}) => {
  let apiurl = useSelector(Sel.apiurl)
  let [imgsize, setImgsize] = useState(5) // multiplied with 25 => in pixel

  let {data} = useApi('pbox/selectImages', {otid, pbtype: opbtype})
  let page_otids = data?.page_otids ?? []

  let [limit, setLimit] = useState(100)
  let [ref, onScreen] = useInView({triggerOnce: false})
  useEffect(() => {
    if (onScreen) {
      setLimit(limit + 100)
    }
  }, [onScreen])
  // only render some
  page_otids = page_otids.slice(0, limit)

  let page_nodes = useTreeNodes(page_otids, {
    datatype: 3,
    fields: [
      'odid',
      'datatype',
      'subtype',
      'caption',
      'op.imgwidth',
      'op.imgheight',
      'op.paginationcaption',
    ],
  })
  let pages = page_otids
    .filter(otid => size(page_nodes[otid]) > 0)
    .map(otid => (
      <Card key={otid} onClick={() => onSelect(otid)}>
        <Card.Content>
          <Image
            width={`${imgsize * 25}px`}
            loading="lazy"
            floated="left"
            src={apiurl.href(`/download/webcache/1000/${otid}`)}
          />
          <Card.Header>
            <TreeNode node={page_nodes[otid]} />
          </Card.Header>
          <Card.Meta>
            {`${page_nodes[otid].op_imgwidth}x${page_nodes[otid].op_imgheight}`}
          </Card.Meta>
        </Card.Content>
      </Card>
    ))

  //~ vl.pprint({page_nodes,page_otids})
  let menu = (
    <XMenu>
      <XMenu.Slider min={1} onValueChange={setImgsize} />
    </XMenu>
  )
  return (
    <HeaderSegment header="Auswahl" icon="mouse pointer" menu={menu}>
      <Card.Group>
        {pages}
        <span key={limit} ref={ref} />
      </Card.Group>
    </HeaderSegment>
  )
}

const decodeIdSituation = node => {
  let parentNode = node.ancestors[0]

  let [page_otid, rel_otid, link_otid] = [undefined, undefined, undefined]

  if (node.datatypeName == 'page') {
    page_otid = node.otid
    if (parentNode?.datatypeName == 'presentation') {
      link_otid = node.link_otid
    }
  } else {
    rel_otid = node.otid
  }
  return {page_otid, rel_otid, link_otid}
}

const PageboxPanel = ({node, allowed_boxtypes}) => {
  let i18n = useSelector(Sel.i18n)

  let boxtypes = BOXTYPES(i18n)
  // determine active boxtype
  let selected_type = keys(allowed_boxtypes)[0]

  let vid = 'pagebox'
  let {page_otid, rel_otid, link_otid} = decodeIdSituation(node)
  let {data, reload} = useApi(
    'pbox/getBoxes',
    {page_otid: link_otid || page_otid, rel_otid},
    {autoreset: false}
  )
  let pageBoxes = data?.pageBoxes || []

  let allowIdChange = !page_otid
  let [changedPageId, setChangedPageId] = useResettableState(null, [node.otid, data])
  if (!allowIdChange) {
    changedPageId = null
  }
  let [changedBox, setChangedBox] = useResettableState(null, [node.otid, changedPageId, data])

  // initialize
  let [opbid, pbox_left, pbox_top, pbox_right, pbox_bottom] = [null, 0, 0, 0, 0]

  let selected_boxconfig = boxtypes[selected_type]
  let selected_opbtype = selected_boxconfig.opbtype
  let pboxes = pageBoxes.filter(pbox => pbox.type == selected_opbtype)
  // max 1 if 1:1 relation (maybe 1:n relation for some pbox types)
  let pbox = pboxes[0]
  if (pbox && (!changedPageId || pbox.otid == changedPageId)) {
    opbid = pbox.id
    if (!link_otid) {
      page_otid = pbox.otid
    }
    ;[pbox_left, pbox_top, pbox_right, pbox_bottom] = pbox.bbox
  }

  //~ let rel_otid = node.otid
  if (changedPageId) {
    page_otid = changedPageId
  }

  let [collapsed, setCollapsed] = usePersistent(
    'dashpanelPagebox_${selected_type}_collapsed',
    !!selected_boxconfig.collapsed
  )
  let collapseButton = (
    <XMenu.Toggle icon="edit" value={!collapsed} set={value => setCollapsed(!value)} />
  )

  let {op_imgwidth, op_imgheight} =
    useTreeNodeFields({otid: page_otid, datatype: 3}, ['op.imgwidth', 'op.imgheight']) || {}

  let {callApi: callSaveApi} = useCallApi('pbox/saveBox')
  let {callApi: callDeleteApi} = useCallApi('pbox/deleteBox')

  let changed = changedBox // || changedPageId

  let save = () => {
    //~ vl.pprint(changedBox)
    callSaveApi(
      {
        pbtype: selected_opbtype,
        page_otid: link_otid || page_otid,
        rel_otid,
        left: changedBox.x1,
        top: changedBox.y1,
        right: changedBox.x2,
        bottom: changedBox.y2,
      },
      reload
    )
  }

  let menu = (
    <XMenu size="small" padded>
      <XMenu.Item icon="save" disabled={!changed} onClick={save} />
      <XMenu.Item
        icon="undo"
        disabled={!changed}
        onClick={() => {
          setChangedBox(null)
          setChangedPageId(null)
        }}
      />

      {allowIdChange && (
        <XMenu.Input
          label="Seite"
          value={page_otid || ''}
          onValueChange={setChangedPageId}
          debounce={1000}
          width="100px"
        />
      )}

      {allowIdChange && (
        <XMenu.DialogItem
          // closes dialog if change on changedPageId
          key={`select_${rel_otid}_${changedPageId}`}
          icon="mouse pointer"
          name="ImageSelect"
          title="Choose image"
        >
          <ImageSelect otid={rel_otid} onSelect={setChangedPageId} opbtype={selected_opbtype} />
        </XMenu.DialogItem>
      )}
      <XMenu.DialogItem
        key={`preview_${rel_otid}`}
        disabled={!opbid}
        icon="eye"
        name="ImagePreview"
        dlgMode="modal"
        title="Preview image"
      >
        <ImagePreview opbid={opbid} />
      </XMenu.DialogItem>
      <XMenu.Item
        icon="trash"
        disabled={!opbid}
        onClick={() =>
          callDeleteApi(
            {
              pbtype: selected_opbtype,
              page_otid: link_otid || page_otid,
              rel_otid,
            },
            reload
          )
        }
        title="Clear image selection"
      />
      {collapseButton}
    </XMenu>
  )
  let content = <Message info>Keine Seitenquelle ausgewählt.</Message>
  let dash_props = {}

  if (page_otid && op_imgwidth && !collapsed) {
    dash_props = {fullscreenable: true, flex: true, height: 300}
    let options = {zoom: {mode: 'areaHeight'}}
    let bbox = {
      x1: pbox_left || parseInt(op_imgwidth * 0.1),
      y1: pbox_top || parseInt(op_imgheight * 0.1),
      x2: pbox_right || parseInt(op_imgwidth * 0.9),
      y2: pbox_bottom || parseInt(op_imgheight * 0.9),
    }
    content = (
      <BoundingBoxViewer
        vid={vid}
        editable
        otid={page_otid}
        bbox={changedBox || bbox}
        isModified={!!changedBox}
        flex
        options={options}
        onChange={setChangedBox}
      />
    )
  }
  if (collapsed) {
    content = null
    menu = (
      <XMenu size="small" padded>
        {collapseButton}
      </XMenu>
    )
  }
  return (
    <DashSegment header={boxtypes[selected_type].label} menu={menu} {...dash_props} fullscreenable>
      {content}
    </DashSegment>
  )
}

export const PageboxDashpanel = {
  Comp: ({i18n}) => {
    /* select/update pagebox geometry
     */
    let node = usePhysicalNode()
    let parentNode = node.ancestors[0]

    const boxtypes = BOXTYPES(i18n)
    // for node type
    let is_allowed = bt => {
      let parent_check = isNil(bt.parentType) || bt.parentType.includes(parentNode?.type)
      let link_check =
        isNil(bt.isLink) || (bt.isLink && !!node.link_otid) || (!bt.isLink && !node.link_otid)
      let type_check = bt.datatype.includes(node.datatype) || bt.type?.includes(node.type)

      return parent_check && link_check && type_check
    }

    let allowed_boxtypes = dict(filterItems(boxtypes, (k, v) => is_allowed(v)))
    if (size(allowed_boxtypes) == 0) return null
    return <PageboxPanel node={node} allowed_boxtypes={allowed_boxtypes} />
  },
  align: ({node}) => {
    if (['presentation', 'page'].includes(node.datatypeName)) {
      return ['right', -1]
    }
    return 'left'
  },
  acl: 'g:admin',
  enabled: ({node}) => ['presentation', 'topstructure', 'page'].includes(node.datatypeName),
}
